import React, { useLayoutEffect, useState } from 'react'
import { FiMinus, FiPlus, FiRefreshCw } from 'react-icons/fi'
import Flex from 'typescript-styled-flex'
import { isSafari } from '../../DetectBrowser/DetectBrowser'
import { theme } from '../../../theme'
import { DecreaseButton, IncreaseButton } from './Button'
import Input from './Input'
import { isValidForMaxValues, isValidForMinValues } from './isValidNumber'
import Wrapper from './Wrapper'
import { INumberInputProps } from './index'
import SpinningIcon from '../../Template/SpinningIcon'
import CalculatorIcon from '../../../assets/icons/calculator.png'
import AddButton from '../../AddButton'

function NumberInput({
                       name,
                       placeholder,
                       disabledInput,
                       isSelected,
                       errorMsg,
                       warningMsg,
                       value,
                       minValueForNumber,
                       maxValueForNumber,
                       onChange,
                       onBlur,
                       size = 1,
                       readonly,
                       onFocus,
                       style,
                       className,
                       width,
                       countIsToLarge,
                       withoutPicker = false,
                       disabled,
                       onlyInteger,
                       isSmallReservation,
                       withAfterIcon,
                       inputStyle,
                       onCalculate,
                       isCalculating,
                       onRefresh,
                       afterLabel = null,
                       dontClampNan = false,
                       decimal,
                       disableMinValueOnInput = false,
                       autoConvert = true,
                       units = '',
                     }: INumberInputProps) {
  const [afterLabelRef, setAfterLabelRef] = useState(null as HTMLDivElement)
  const [afterLabelWidth, setAfterLabelWidth] = useState(0)

  function clamp(num: number) {
    if (dontClampNan && isNaN(num)) {
      return num
    }
    if (onlyInteger) {
      num = Math.floor(num)
    }
    if (decimal) {
      num = Number(num.toFixed(decimal))
    }
    return !isValidForMinValues(num, minValueForNumber)
      ? minValueForNumber
      : !isValidForMaxValues(num, maxValueForNumber)
        ? maxValueForNumber
        : num
  }

  function sanitizeTextValue(textValue: string) {
    if (/^0[0-9]+/.test(textValue)) {
      return '0'
    }

    return textValue
  }

  const inputValue = value === '' ? '' : value

  React.useEffect(() => {
    if (value === '') {
      return
    }
    const valueIsANumber = typeof value === 'number'
    if (!valueIsANumber && autoConvert) {
      onChange(Number(sanitizeTextValue(value)))
      return
    }
    const clamped = clamp(Number(value))
    if (clamped !== value && autoConvert) {
      onChange(clamped)
    }
  })

  if (countIsToLarge) {
    inputStyle = { ...inputStyle, color: theme.colors.palette.reddish }
  }

  const afterLabelShown = afterLabel && inputValue !== ''

  if (afterLabelWidth) {
    inputStyle = {
      ...inputStyle,
      paddingRight: `${afterLabelWidth + 14}px`,
      width: `calc(100%-${afterLabelWidth}px`,
    }
  }

  useLayoutEffect(() => {
    if (afterLabelRef) {
      const rect = afterLabelRef.getBoundingClientRect()

      setAfterLabelWidth(rect.width)
    } else {
      setAfterLabelWidth(0)
    }
  }, [value, afterLabel, withoutPicker, afterLabelRef])

  function change(e: React.SyntheticEvent<HTMLInputElement>) {
    if (disableMinValueOnInput && e.currentTarget.value === '') {
      onChange('')
      return
    }

    const changedValue =
      e.currentTarget.value === '' && withoutPicker
        ? ''
        : clamp(Number(e.currentTarget.value))
    onChange(changedValue)
  }

  function increment() {
    if (!disabled) {
      if (!(value >= maxValueForNumber)) {
        onFocus()
        onChange(value + 1)
      } else if (!value) {
        onFocus()
        onChange(minValueForNumber)
      }
    }
  }

  function decrement() {
    if (!disabled) {
      if (!(value <= minValueForNumber)) {
        onFocus()
        onChange(value - 1)
      } else if (!value) {
        onFocus()
        onChange(minValueForNumber)
      }
    }
  }

  function blur(e) {
    if (value === 0 && withoutPicker) {
      onChange(0)
    }
    if (disableMinValueOnInput) {
      onBlur && onBlur(undefined)
      return
    }
    onBlur && onBlur(e)
  }

  function FilterInput(event) {
    const { key } = event
    const keyCode = 'which' in event ? event.which : event.keyCode
    const isSpecial = keyCode < 48
    const isNumber = '0123456789'.includes(key)
    const isMinusSign = key === '-'
    const isPeriod = key === '.'
    const isComma = key === ','


    if (
      !(
        isNumber ||
        isSpecial ||
        (!onlyInteger && (isPeriod || isComma)) ||
        ((minValueForNumber === undefined || minValueForNumber < 0) &&
          isMinusSign)
      )
    ) {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  function handlePaste(e) {
    // Get pasted data via clipboard API
    const { clipboardData } = e
    const pastedData = clipboardData.getData('Text').toUpperCase()

    if (pastedData.indexOf('E') > -1) {
      //alert('found an E');
      e.stopPropagation()
      e.preventDefault()
    }

    const pastedNumber = Number(pastedData)
    const clamped = clamp(pastedNumber)
    if (clamped !== pastedNumber) {
      e.stopPropagation()
      e.preventDefault()
    }
  }

  return (
    <Wrapper
      smallReservation={isSmallReservation}
      style={{
        minHeight: size ? `${38 * size}px` : '38px',
        ...style,
        position: 'relative',
        background: disabled ? '#f5f5f5' : 'transparent'
      }}
      maxWidth={withoutPicker && width}
      disabled={disabled}
      safari={isSafari()}
      size={size}
      selected={isSelected}
      error={!!errorMsg || !!countIsToLarge ? true : false}
      warning={warningMsg && warningMsg.length > 0}
      withoutPicker={withoutPicker}
    >
      {!withoutPicker && (
        <DecreaseButton
          onClick={decrement}
          className={`${className}-button-decrese`}
          data-cy={`${className}-button-decrese`}
          size={size}
        >
          <FiMinus />
        </DecreaseButton>
      )}
      <Input
        withoutPicker={withoutPicker}
        name={name}
        style={inputStyle}
        placeholder={placeholder}
        readOnly={readonly}
        selected={isSelected}
        error={!!errorMsg || !!countIsToLarge}
        warning={warningMsg && warningMsg.length > 0}
        type={'number'}
        min={minValueForNumber || undefined}
        value={inputValue}
        size={size}
        onChange={change}
        onKeyDown={FilterInput}
        onPaste={handlePaste}
        onFocus={onFocus}
        onBlur={blur}
        step={!!onlyInteger ? 1 : null}
        pattern={!!onlyInteger ? '\\d*' : undefined}
        smallReservation={isSmallReservation}
        disabled={disabled||disabledInput}
        className={className}
        data-cy={`${className}-number-input`}
      />
      {onCalculate && (
        <AddButton
          color={
            warningMsg
              ? theme.colors.palette.tangerine
              : theme.colors.palette.emerald
          }
          style={{
            borderRadius: '0px 3px 3px 0px',
            height: `${38 * size}px`,
            minWidth: `${38 * size}px`,
            width: `${38 * size}px`,
          }}
          onClick={isCalculating ? onRefresh : onCalculate}
        >
          {isCalculating ? (
            <SpinningIcon size={16 * size} />
          ) : warningMsg ? (
            <FiRefreshCw size={16 * size} />
          ) : (
            <img
              style={{
                filter:
                  'invert(100%) sepia(25%) saturate(729%) hue-rotate(212deg) brightness(109%) contrast(101%)',
              }}
              width={24 * size}
              height={24 * size}
              src={CalculatorIcon}
              alt={'main price'}
            />
          )}
        </AddButton>
      )}
      {withAfterIcon && (
        <Flex
          style={{ cursor: 'pointer', position: 'absolute', right: '10px' }}
        >
          {withAfterIcon}
        </Flex>
      )}
      {units && (
        <Flex
          style={{ position: 'absolute', right: '18px',
            fontFamily: 'Montserrat',
            fontSize: '12px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: 'normal',
            letterSpacing: '0.024px',
            color: '#C2C2C2',
          }}
        >
          <div style={{
            width: '1px',
            height: '15px',
            background: '#D7D7D7',
            marginRight:'15px',
          }}/>
          {units}
        </Flex>
      )}
      <div
        style={{
          color: '#969696',
          display: afterLabelShown ? 'initial' : 'none',
          fontSize: `${13 * size}px`,
          position: 'absolute',
          right: '10px',
        }}
        ref={setAfterLabelRef}
      >
        {afterLabel}
      </div>
      {!withoutPicker && (
        <IncreaseButton
          onClick={!(value >= maxValueForNumber) ? increment : null}
          size={size}
          className={`${className}-button-increse`}
          data-cy={`${className}-button-increse`}
        >
          <FiPlus />
        </IncreaseButton>
      )}
    </Wrapper>
  )
}

export default NumberInput
